import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ExistingBorrowerStatus } from 'api/curo/ApplyProcessApi';
import FormTitle from 'components/PageLayout/FormTitle';
import ContactInformationForm from 'components/curo-components/forms/ContactInformationForm';
import {
  ContactInformationFields,
  IContactInformationFormValues,
} from 'components/curo-components/forms/ContactInformationForm/constants';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroAppRoute } from 'enums/curo/CuroAppRoute';
import { EnvironmentVariable } from 'enums/curo/EnvironmentVariable';
import { envs } from 'envs';
import { setContactInformationData } from 'handlers/curo-handlers/applyProcessSlice';
import { getApplyProcessState } from 'handlers/curo-handlers/selectors';
import { setEmail, setPhone } from 'handlers/curo-handlers/signInProcessSlice';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { useCurrentStep } from 'hooks/useCurrentStep';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import useUtmSource from 'hooks/useUtmSource';
import LoaderStep from 'pages/CuroPages/ApplicationProcess/steps/LoaderStep';
import {
  BorrowerLockedPage,
  ExistingAccountWithAnotherEmailOrPhone,
} from 'pages/CuroPages/EndPages';
import { IDefaultStepProps } from 'pages/CuroPages/types';
import {
  checkExistingAccount,
  checkExistingBorrower,
  createAccount,
  createApplication,
  createBorrower,
  updateBorrower,
  verifySession,
} from 'thunks';
import {
  mapFormFieldsToCheckExistingBorrowerRequest,
  mapFormFieldsToCreateAccountRequest,
  mapFormFieldsToCreateApplicationRequest,
  mapFormFieldsToCreateBorrowerRequest,
} from 'utils/curo-helpers/contactInformationFormHelpers';

import ConfirmContactInformationModal from './ConfirmContactInformationModal';

interface IContactInformationFormStepProps extends IDefaultStepProps {
  onBackButtonClick: () => void;
}

const PHONE_NUMBER_IN_USE_ERROR_MESSAGE = `The phone number you entered is already in use. Please provide a different phone number. If you have any questions, please call ${
  envs[EnvironmentVariable.CuroContactPhoneNumber]
}.`;
const EMAIL_IN_USE_ERROR_MESSAGE = `The email you entered is already in use. Please provide a different email. If you have any questions, please call ${
  envs[EnvironmentVariable.CuroContactPhoneNumber]
}.`;

enum ContactInformationFormSteps {
  ContactInformationFormStep = 'ContactInformationFormStep',
  ExistingAccountWithAnotherEmailOrPhoneStep = 'ExistingAccountWithAnotherEmailOrPhoneStep',
  BorrowerLockedPageStep = 'BorrowerLockedPageStep',
}

type ContactInformationFormErrors = Record<ContactInformationFields, string>;

const INITIAL_CONTACT_INFORMATION_FORM_ERRORS: ContactInformationFormErrors = {
  [ContactInformationFields.BorrowerPhone]: '',
  [ContactInformationFields.BorrowerEmail]: '',
  [ContactInformationFields.TelephoneAndElectronicCommunicationPolicy]: '',
};

const PAGE_TITLE = 'Contact Information';

const ContactInformationFormStep = ({
  onBackButtonClick,
  onStepComplete,
}: IContactInformationFormStepProps) => {
  const trackEvent = useGoogleAnalytics();
  const { getUtmData } = useUtmSource();
  const [currentStep, setCurrentStep] = useCurrentStep<ContactInformationFormSteps>(
    ContactInformationFormSteps.ContactInformationFormStep,
  );
  const [contactInformationFormErrors, setContactInformationFormErrors] =
    useState<ContactInformationFormErrors>(INITIAL_CONTACT_INFORMATION_FORM_ERRORS);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const { applyProcessData, contactInformationData } = useAppSelector(getApplyProcessState);
  const dispatch = useAppDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();

  const isEmailAndPhoneProvided = !!(
    contactInformationData[ContactInformationFields.BorrowerEmail] &&
    contactInformationData[ContactInformationFields.BorrowerPhone]
  );

  const [isLoading, setIsLoading] = useState<boolean>(isEmailAndPhoneProvided);

  const checkBorrowerExistence = async () => {
    const borrowerExistenceCheckData = mapFormFieldsToCheckExistingBorrowerRequest({
      ...contactInformationData,
      ...applyProcessData,
    });

    const { borrowerId, existingBorrowerStatus } = await dispatchWithUnwrap(
      checkExistingBorrower(borrowerExistenceCheckData),
    );

    if (existingBorrowerStatus === ExistingBorrowerStatus.PhoneNumberInUse) {
      setContactInformationFormErrors((prev) => ({
        ...prev,
        [ContactInformationFields.BorrowerPhone]: PHONE_NUMBER_IN_USE_ERROR_MESSAGE,
      }));
      setIsConfirmModalOpen(false);
      return {
        shouldAbort: true,
        borrowerId: null,
      };
    }

    if (existingBorrowerStatus === ExistingBorrowerStatus.EmailInUse) {
      setContactInformationFormErrors((prev) => ({
        ...prev,
        [ContactInformationFields.BorrowerEmail]: EMAIL_IN_USE_ERROR_MESSAGE,
      }));
      setIsConfirmModalOpen(false);

      return {
        shouldAbort: true,
        borrowerId: null,
      };
    }

    if (
      existingBorrowerStatus === ExistingBorrowerStatus.AccountWithDifferentPhoneNumber ||
      existingBorrowerStatus === ExistingBorrowerStatus.AccountWithDifferentEmail
    ) {
      setCurrentStep(ContactInformationFormSteps.ExistingAccountWithAnotherEmailOrPhoneStep);
      setIsConfirmModalOpen(false);

      return {
        shouldAbort: true,
        borrowerId: null,
      };
    }

    if (existingBorrowerStatus === ExistingBorrowerStatus.Locked) {
      setCurrentStep(ContactInformationFormSteps.BorrowerLockedPageStep);
      return {
        shouldAbort: true,
        borrowerId: null,
      };
    }

    return {
      shouldAbort: false,
      borrowerId,
    };
  };

  const createOrUpdateBorrower = async ({ borrowerId }: { borrowerId: string | null }) => {
    const borrowerData = mapFormFieldsToCreateBorrowerRequest({
      ...contactInformationData,
      ...applyProcessData,
    });

    const borrower = borrowerId
      ? await dispatchWithUnwrap(
          updateBorrower({
            borrowerId,
            ...borrowerData,
          }),
        )
      : await dispatchWithUnwrap(createBorrower(borrowerData));

    return borrower;
  };

  const createBorrowerApplication = async (borrowerId: string) => {
    const applicationData = mapFormFieldsToCreateApplicationRequest({
      borrowerId,
      values: {
        ...applyProcessData,
        ...contactInformationData,
      },
      utmData: getUtmData(),
    });

    return dispatchWithUnwrap(createApplication(applicationData));
  };

  const createBorrowerAccount = async (borrowerId: string) => {
    const accountData = mapFormFieldsToCreateAccountRequest(borrowerId, contactInformationData);

    await dispatchWithUnwrap(createAccount(accountData));
  };

  const handleContinue = async () => {
    try {
      const { borrowerId: existingBorrowerId, shouldAbort } = await checkBorrowerExistence();

      if (shouldAbort) {
        setIsLoading(false);
        return;
      }

      const borrower = await createOrUpdateBorrower({
        borrowerId: existingBorrowerId,
      });

      const { applicationDisplayId } = await createBorrowerApplication(borrower.borrowerId);

      const { isAccountExists, isEmailVerified, isPhoneVerified } = await dispatchWithUnwrap(
        checkExistingAccount(borrower.borrowerId),
      );

      trackEvent(GoogleAnalyticsEventType.CreatedApp, {
        isEmailVerified,
        isPhoneVerified,
        applicationDisplayId,
      });

      if (isAccountExists) {
        const { isSessionVerified } = await dispatchWithUnwrap(
          verifySession({
            borrowerId: borrower.borrowerId,
          }),
        );

        if (!isSessionVerified) {
          return onStepComplete();
        }

        return navigate(CuroAppRoute.ResumeProcess);
      }

      await createBorrowerAccount(borrower.borrowerId);

      return navigate(CuroAppRoute.ResumeProcess);
    } catch (error) {
      if (isConfirmModalOpen) {
        setIsConfirmModalOpen(false);
      }
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values: IContactInformationFormValues) => {
    await dispatch(setContactInformationData(values));
    await dispatch(setPhone({ phone: values[ContactInformationFields.BorrowerPhone] }));
    await dispatch(setEmail({ email: values[ContactInformationFields.BorrowerEmail] }));

    setIsConfirmModalOpen(true);
  };

  const handleResetContactInformationFormErrors = (fieldName: ContactInformationFields) => {
    setContactInformationFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
  };

  useEffect(() => {
    if (isEmailAndPhoneProvided && isLoading) {
      handleContinue();
    }
    if (!isEmailAndPhoneProvided || !isLoading) {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessContactInfo);
    }
  }, []);

  if (isLoading && isEmailAndPhoneProvided) {
    return <LoaderStep />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case ContactInformationFormSteps.ContactInformationFormStep:
        return (
          <>
            <FormTitle title={PAGE_TITLE} />
            <ContactInformationForm
              handleSubmit={handleSubmit}
              initialData={contactInformationData}
              onBackButtonClick={onBackButtonClick}
              externalErrors={contactInformationFormErrors}
              resetExternalErrors={handleResetContactInformationFormErrors}
            />
            {isConfirmModalOpen && (
              <ConfirmContactInformationModal
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleContinue}
              />
            )}
          </>
        );

      case ContactInformationFormSteps.ExistingAccountWithAnotherEmailOrPhoneStep:
        return (
          <ExistingAccountWithAnotherEmailOrPhone
            onButtonClick={() => {
              setCurrentStep(ContactInformationFormSteps.ContactInformationFormStep);
            }}
          />
        );
      case ContactInformationFormSteps.BorrowerLockedPageStep:
        return <BorrowerLockedPage />;
      default:
        return null as never;
    }
  };

  return renderStep();
};

export default ContactInformationFormStep;
